import { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useLatest } from 'ahooks';
import type { VidSrc } from '@archipro-design/aria';
import {
    useAppDisplayModeContext,
    useStyles,
    VideoPlayer,
} from '@archipro-design/aria';
import { useTracker } from '@archipro-website/tracker';
import useHiResImage from '@modules/product/hook/use-hi-res-image';
import { getThumbnailImage } from '@modules/product/util/get-thumbnail-image';
import type { LibraryVideo } from '@modules/product/type/product-gallery-types';
import * as S from './VideoSlide.style';

interface VideoSlideProps {
    libraryVideo: LibraryVideo;
    /** Notified when playback starts/stops — lets a parent overlay (e.g. the
     *  revamp gallery's play indicator) hide itself while the video plays. */
    onPlayingChange?: (playing: boolean) => void;
    /** Suppress the player's own play icon on every breakpoint — for callers
     *  that layer their own indicator (the revamp gallery), which would
     *  otherwise double up with the native one on mobile. */
    hideNativePlayIcon?: boolean;
    /**
     * Play the video in the slide rather than in the player's own modal.
     *
     * Left off, `VideoPlayer` renders the video only inside a Fluent dialog
     * portaled to `document.body`, which is right for a video sitting in the
     * page and wrong inside a fullscreen viewer: the dialog paints under the
     * viewer's own surface, takes no clicks through the viewer's
     * `pointer-events: none` on the body, and brings a second body scroll lock
     * that outlives the viewer's. A host that already covers the screen passes
     * this so the player stays where the reader is looking.
     */
    playsInline?: boolean;
}

const VideoSlide = ({
    libraryVideo,
    onPlayingChange,
    hideNativePlayIcon = false,
    playsInline = false,
}: VideoSlideProps) => {
    // Whether the player is up. Inline this is the video itself; otherwise it
    // is the modal's open state.
    const [shouldPlay, setShouldPlay] = useState(false);
    // Whether it is actually running, which a pause ends without taking the
    // player down with it.
    const [playing, setPlaying] = useState(false);
    const { css } = useStyles();
    const { mobile } = useAppDisplayModeContext();

    const tracker = useTracker();

    const getImageRef = useLatest(getThumbnailImage);

    const { image } = getImageRef.current(libraryVideo);
    const { VideoID, Type } = libraryVideo;
    const imgSrc = image.Desktop ?? '';

    const { hiResImage, fallback } = useHiResImage(imgSrc);

    const [videoKey, setVideoKey] = useState(0);
    useEffect(() => {
        if (!mobile) {
            setVideoKey((prevKey) => prevKey + 1);
        }
    }, [hiResImage, mobile]);

    const [observerRef, inView] = useInView({
        rootMargin: '50% 0px',
    });
    useEffect(() => {
        if (!inView) {
            setShouldPlay(false);
            setPlaying(false);
        }
    }, [inView]);

    const onPlayingChangeRef = useLatest(onPlayingChange);
    useEffect(() => {
        onPlayingChangeRef.current?.(playing);
    }, [playing, onPlayingChangeRef]);

    const extraVideoProps = {
        playing: shouldPlay,
        muted: true,
        controls: true,
        playsInline,
        onPlay: () => {
            setShouldPlay(true);
            setPlaying(true);
        },
        // Lets the revamp gallery's play indicator reappear when the shopper
        // pauses via the player controls (not just on scroll-out). Only hosts
        // that observe playback opt in — the legacy gallery has no indicator to
        // restore and kept `shouldPlay` true through a pause.
        //
        // A pause reports that playback stopped; it does not take the player
        // down. Inline, `shouldPlay` is what keeps the video uncovered, so
        // clearing it would pull the poster back over a video the reader meant
        // to hold on. In the modal it is what closes it, which is what a pause
        // has always done there.
        onPause: onPlayingChange
            ? () => {
                  setPlaying(false);
                  if (!playsInline) {
                      setShouldPlay(false);
                  }
              }
            : undefined,
        trackPlayEvent: () => {
            tracker.log('Video', {
                url: new URL(window.location.href),
            });
        },
    };

    const videoType = Type.toLowerCase() as VidSrc['type'];

    return (
        <div ref={observerRef} className={css(S.VideoContainer)}>
            <VideoPlayer
                key={videoKey}
                className={css(
                    playsInline ? S.VideoPlayerInline : S.VideoPlayer,
                )}
                src={{
                    videoID: VideoID,
                    type: videoType,
                }}
                coverImage={{
                    src: mobile ? imgSrc : hiResImage,
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    objectPosition: 'center',
                    alt: libraryVideo.Title || '',
                    onError: fallback,
                    variables: {
                        placeholderBackgroundColorLight: 'transparent',
                    },
                }}
                {...extraVideoProps}
                variables={{
                    playIconSizeDesktop: 80,
                    playIconSizeMobile: 80,
                }}
                playIcon={hideNativePlayIcon || !mobile ? <span /> : undefined}
            />
        </div>
    );
};

export default VideoSlide;
